export type SessionImprovement = {
  youSaid: string;
  better: string;
  why: string;
};

export type SessionAnalysis = {
  summary: string;
  overall: number | null;
  tooShort: boolean;
  improvements: SessionImprovement[];
  nextSlug?: string;
  handled?: string;
  stuck?: string;
  coachLine?: string;
  repaired?: string;
  uncertain?: boolean;
  confidenceScore?: number;
};

const MAX_IMPROVEMENTS = 2;

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asScore(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function asConfidenceScore(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value);
  if (rounded < 1 || rounded > 5) return undefined;
  return rounded;
}

export function countUserTurns(
  transcript: Array<{ role?: string; text?: string }>,
): number {
  return transcript.filter((turn) => {
    if (turn.role !== "user") return false;
    const text = (turn.text ?? "").replace(/\[final\]/g, "").trim();
    return text.length > 0;
  }).length;
}

export function isTooShortForFeedback(userTurns: number): boolean {
  return userTurns < 2;
}

export const TOO_SHORT_SUMMARY = "Ficou curto — fala mais um pouco que eu te devolvo o feedback.";

export function parseSessionAnalysis(raw: unknown): SessionAnalysis | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const summary = asString(row.summary);
  if (!summary) return null;

  const improvementsRaw = Array.isArray(row.improvements) ? row.improvements : [];
  const improvements = improvementsRaw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const rowItem = item as Record<string, unknown>;
      const youSaid = asString(rowItem.youSaid ?? rowItem.you_said);
      const better = asString(rowItem.better);
      const why = asString(rowItem.why);
      if (!youSaid || !better || !why) return null;
      return { youSaid, better, why };
    })
    .filter((item): item is SessionImprovement => item !== null)
    .slice(0, MAX_IMPROVEMENTS);

  const stuck = asString(row.stuck ?? row.travou) || undefined;
  const uncertain = row.uncertain === true || row.low_confidence === true || row.lowConfidence === true;

  return {
    summary,
    overall: asScore(row.overall),
    tooShort: row.tooShort === true || row.too_short === true,
    improvements,
    nextSlug: asString(row.nextSlug ?? row.next_slug) || undefined,
    handled: asString(row.handled ?? row.se_virou ?? row.seVirou) || undefined,
    stuck,
    coachLine: asString(row.coachLine ?? row.coach_line) || undefined,
    repaired: asString(row.repaired ?? row.repair ?? row.se_recuperou) || undefined,
    uncertain: uncertain || undefined,
    confidenceScore: asConfidenceScore(row.confidenceScore ?? row.confidence_score),
  };
}

export function fallbackAnalysis(input: {
  userTurns: number;
  situationTitle?: string;
  nextSlug?: string;
  overall?: number;
  handled?: string;
  stuck?: string;
  coachLine?: string;
  repaired?: string;
  uncertain?: boolean;
}): SessionAnalysis {
  if (isTooShortForFeedback(input.userTurns)) {
    return {
      summary: TOO_SHORT_SUMMARY,
      overall: null,
      tooShort: true,
      improvements: [],
      nextSlug: input.nextSlug,
      uncertain: true,
    };
  }

  const title = input.situationTitle ? ` em "${input.situationTitle}"` : "";
  const stuck = input.stuck?.trim() || undefined;
  const uncertain = input.uncertain === true || !stuck;
  return {
    summary: `Você praticou${title} e registrei a conversa.`,
    overall: input.overall ?? null,
    tooShort: false,
    improvements: [],
    nextSlug: input.nextSlug,
    handled: input.handled,
    stuck,
    coachLine: input.coachLine,
    repaired: input.repaired,
    uncertain,
  };
}
